import { Component, ElementRef, computed, inject, input, output, signal } from '@angular/core';
import {
  ORDER_STATUS_OPTIONS,
  getOrderStatusOption,
  isDestructiveStatus,
} from '../order-status';
import { IconComponent } from '../../../shared/icon/icon.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-status-select',
  standalone: true,
  imports: [IconComponent, ConfirmDialogComponent],
  templateUrl: './status-select.component.html',
  styleUrl: './status-select.component.css',
  host: {
    class: 'relative inline-block',
    '(document:click)': 'onDocClick($event)',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class StatusSelectComponent {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly status = input.required<string>();
  /** Whether the order's photos have already been permanently deleted. */
  readonly photosDeleted = input(false);
  readonly statusChange = output<string>();

  protected readonly options = ORDER_STATUS_OPTIONS;
  protected readonly getOrderStatusOption = getOrderStatusOption;

  protected readonly open = signal(false);
  protected readonly pendingStatus = signal<string | null>(null);

  protected readonly selected = computed(() => getOrderStatusOption(this.status()));
  protected readonly pendingOption = computed(() => {
    const value = this.pendingStatus();
    return value ? getOrderStatusOption(value) : undefined;
  });

  protected toggle(): void {
    this.open.update(v => !v);
  }

  protected select(value: string): void {
    this.open.set(false);

    if (value === this.status()) {
      return;
    }

    // Destructive statuses trigger permanent photo deletion server-side, so confirm first
    // — unless the photos are already deleted, in which case there's nothing to warn about.
    if (isDestructiveStatus(value) && !this.photosDeleted()) {
      this.pendingStatus.set(value);
      return;
    }

    this.statusChange.emit(value);
  }

  protected confirm(): void {
    const value = this.pendingStatus();
    this.pendingStatus.set(null);
    if (value) {
      this.statusChange.emit(value);
    }
  }

  protected cancel(): void {
    this.pendingStatus.set(null);
  }

  protected onDocClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }

  protected onEscape(): void {
    this.open.set(false);
    this.pendingStatus.set(null);
  }
}
